import React from 'react';
import { MC } from './theme';
import { LANE_ALL } from './laneFilter';

/**
 * THE TWO-STAGE LANE FILTER — REGION, THEN SECTOR.
 *
 * ── THE VISUAL IS THE DESIGN'S, NOT MINE (owner ruling 2026-08-28) ──────────
 * The design draws NO dropdown above any of these four panels — measured, zero
 * `<select>` in the file. So this was brought back as a proposal rather than
 * chosen, and what was approved is the design's own two pieces:
 *
 *   • `.input-like` — h38, radius 12, 1px `--line2`, `rgba(255,255,255,.04)`,
 *     `--muted`, 12px. The ONLY "control that looks like an input" the design
 *     defines. It defines it and never instantiates it; this is its instance.
 *   • the teal wash for a selected state — `.tabs button.active` in the design,
 *     and already the shipped selected state of the Osiris sector lanes. ONE
 *     idiom on this surface, not a second.
 *
 * MC tokens only. No new hex, no className, no webfont — the surface's rules.
 *
 * ── A NATIVE SELECT, DELIBERATELY ──────────────────────────────────────────
 * Not a custom popover. The popovers in this app live on other surfaces and
 * carry another palette, and a hand-rolled listbox would owe focus management
 * and keyboard semantics that the platform already gives correctly. The chrome
 * is restyled onto MC tokens; the behaviour is the browser's.
 *
 * ── IT MEASURES WHETHER IT CAN FILTER, AND SAYS SO WHEN IT CANNOT ──────────
 * `filterable` is not a guess about the backend — it is `laneFilterable(rows)`,
 * asked of the rows actually in hand. Today NO row of any of the four tables
 * carries a region or a sector (counted: 108/108/95/2 rows, none), so this
 * renders disabled with the reason. It lights up by itself the moment the
 * producer emits the keys, with no edit here. That degrade is the surface's own
 * precedent, not an invention: the Osiris lane buttons disable-and-caption on
 * exactly the same measurement.
 */

/** A narrowing is not a starting state, so this is what "not narrowed" looks
 *  like on both axes. Rendered as the first option of each control. */
const ALL_REGIONS = 'All regions';
const ALL_SECTORS = 'All sectors';

export function LaneFilter({
  region, sector, regions, sectors, filterable, onRegion, onSector,
}: {
  region: string;
  sector: string;
  regions: readonly string[];
  sectors: readonly string[];
  filterable: boolean;
  onRegion: (v: string) => void;
  onSector: (v: string) => void;
}) {
  return (
    <div style={S.bar}>
      <Field
        label="Region"
        value={region}
        allLabel={ALL_REGIONS}
        options={regions}
        disabled={!filterable}
        onChange={onRegion}
      />
      <Field
        label="Sector"
        value={sector}
        allLabel={ALL_SECTORS}
        options={sectors}
        // THE SECOND STAGE NARROWS WITHIN THE FIRST. `sectors` is already
        // computed against the chosen region, so a region with no mechanics
        // does not offer Mechanics — the option list is the answer, and there
        // is no second rule here restating it.
        disabled={!filterable}
        onChange={onSector}
      />
      {!filterable && (
        <span style={S.note}>
          Not filterable — no row carries a region or sector yet, so there is
          nothing on the board to narrow by.
        </span>
      )}
    </div>
  );
}

function Field({ label, value, allLabel, options, disabled, onChange }: {
  label: string;
  value: string;
  allLabel: string;
  options: readonly string[];
  disabled: boolean;
  onChange: (v: string) => void;
}) {
  // NARROWED, not merely "has a value". LANE_ALL is the unnarrowed state on
  // both axes, so the teal wash marks a real narrowing and nothing else.
  const narrowed = !disabled && value !== LANE_ALL;
  return (
    <label style={S.field}>
      <span style={S.label}>{label}</span>
      <span style={S.wrap}>
        <select
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          style={{
            ...S.select,
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: disabled ? MC.textFaint : narrowed ? MC.text : MC.muted,
            background: narrowed ? MC.tealSoft : 'rgba(255,255,255,.04)',
            borderColor: narrowed ? MC.tealEdge : MC.line2,
          }}
        >
          {/* THE UNNARROWED OPTION IS ALWAYS FIRST AND ALWAYS PRESENT, so the
              control can always be returned to "everything" — including when
              the current selection has just gone out of scope. */}
          <option value={LANE_ALL} style={S.option}>{allLabel}</option>
          {/* A SELECTION THE ROWS NO LONGER OFFER STILL DISPLAYS. The board can
              refresh out from under a held selection — the region the owner
              chose simply stops being present. Dropping its option would leave
              the control BLANK while that selection was still being applied, so
              it is carried and the table's empty state explains the result. */}
          {narrowed && options.indexOf(value) < 0 && (
            <option value={value} style={S.option}>{value}</option>
          )}
          {options.map((o) => (
            <option key={o} value={o} style={S.option}>{o}</option>
          ))}
        </select>
        <span aria-hidden style={{ ...S.caret, color: disabled ? MC.textFaint : MC.dim }}>▾</span>
      </span>
    </label>
  );
}

const S: Record<string, React.CSSProperties> = {
  bar: {
    display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap',
    margin: '0 0 12px',
  },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  // The Osiris section label's own metrics — 9px, .16em, uppercase, dim.
  label: {
    fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase',
    color: MC.textDim,
  },
  wrap: { position: 'relative', display: 'inline-flex', alignItems: 'center' },
  // `.input-like`, transcribed: h38, radius 12, 1px --line2,
  // rgba(255,255,255,.04), --muted, 12px, 0 12px. The right padding is widened
  // to leave the caret its own room rather than letting a long sector name run
  // underneath it.
  select: {
    appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
    height: 38, borderRadius: 12, borderWidth: 1, borderStyle: 'solid',
    padding: '0 30px 0 12px', fontSize: 12, fontFamily: 'inherit',
    minWidth: 148, outline: 'none',
  },
  // The native option list is painted by the OS, so it is given the panel
  // colours explicitly — otherwise it renders light on a dark surface.
  option: { background: MC.panel2, color: MC.text },
  caret: {
    position: 'absolute', right: 12, fontSize: 10, pointerEvents: 'none',
  },
  note: { fontSize: 11, color: MC.textFaint, paddingBottom: 10 },
};

export default LaneFilter;
